import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { useToast } from "@/hooks/use-toast";
import { AdminUser, AdminUserPayload, UserCategory, adminAPI, getUserData } from "@/lib/api";
import { Eye, ImageUp, Pencil, Plus, Power, PowerOff, Search, Trash2 } from "lucide-react";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

type UserFormState = AdminUserPayload;

const emptyForm: UserFormState = {
  name: "",
  email: "",
  role: "user",
  category: "student",
  status: "active",
};

const normalizeCategory = (category?: string): UserCategory =>
  category === "graduate" ? "graduate" : "student";

const getCategoryLabel = (category?: string) =>
  normalizeCategory(category) === "graduate" ? "admin.categoryGraduate" : "admin.categoryStudent";

const PROFILE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const PROFILE_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);

const toFormState = (user: AdminUser): UserFormState => ({
  name: user.name,
  email: user.email,
  role: user.role,
  category: normalizeCategory(user.category),
  status: user.isActive ? "active" : "inactive",
});

export default function AdminUserManagement() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentUser = getUserData();
  const isAdmin = currentUser?.role === "admin";
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<UserFormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [isUploadingProfileImage, setIsUploadingProfileImage] = useState(false);
  const profileImageInputRef = useRef<HTMLInputElement | null>(null);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) =>
      [
        user.name,
        user.email,
        user.role,
        t(getCategoryLabel(user.category)),
        user.isActive ? t("admin.active") : t("admin.inactive"),
      ]
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [searchQuery, t, users]);

  const loadUsers = async () => {
    if (!isAdmin) return;

    setIsLoading(true);
    try {
      const response = await adminAPI.getUsers({ limit: 100 });
      setUsers(response.data || []);
    } catch (error: any) {
      toast({
        title: t("admin.userLoadFailed"),
        description: getLocalizedErrorMessage(error, "admin.userLoadFailedDesc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [isAdmin]);

  if (!isAdmin) {
    return null;
  }

  const openCreateDialog = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminUser) => {
    setEditingUser(user);
    setForm(toFormState(user));
    setDialogOpen(true);
  };

  const handleFormChange = (field: keyof UserFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast({
        title: t("admin.validationError"),
        description: t("admin.userRequiredFields"),
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload: AdminUserPayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        category: form.category,
        status: form.status,
      };

      const response = editingUser
        ? await adminAPI.updateUser(editingUser._id, payload)
        : await adminAPI.createUser(payload);

      toast({
        title: editingUser ? t("admin.userSaved") : t("admin.userCreated"),
        description: response.message || (editingUser ? t("admin.userSavedDesc") : t("admin.userCreatedDesc")),
      });

      await loadUsers();
      setDialogOpen(false);
    } catch (error: any) {
      toast({
        title: editingUser ? t("admin.userSaveFailed") : t("admin.userCreateFailed"),
        description: getLocalizedErrorMessage(error, "admin.userOperationFailedDesc"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (user: AdminUser) => {
    const nextStatus = user.isActive ? "inactive" : "active";

    try {
      await adminAPI.updateUserStatus(user._id, nextStatus);
      toast({
        title: user.isActive ? t("admin.userDeactivated") : t("admin.userReactivated"),
        description: user.isActive ? t("admin.userDeactivatedDesc") : t("admin.userReactivatedDesc"),
      });
      await loadUsers();
    } catch (error: any) {
      toast({
        title: t("admin.userStatusFailed"),
        description: getLocalizedErrorMessage(error, "admin.userOperationFailedDesc"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    if (deleteTarget.role === "admin") {
      toast({
        title: t("admin.userDeleteFailed"),
        description: t("admin.adminUserNotDeletable"),
        variant: "destructive",
      });
      setDeleteTarget(null);
      return;
    }

    try {
      await adminAPI.deleteUser(deleteTarget._id);
      toast({
        title: t("admin.userDeleted"),
        description: t("admin.userDeletedDesc"),
      });
      setDeleteTarget(null);
      await loadUsers();
    } catch (error: any) {
      toast({
        title: t("admin.userDeleteFailed"),
        description: getLocalizedErrorMessage(error, "admin.userOperationFailedDesc"),
        variant: "destructive",
      });
    }
  };

  const handleViewProfile = (user: AdminUser) => {
    navigate(`/admin/users/${user._id}/profile`);
  };

  const handleProfileImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!editingUser || !file) {
      return;
    }

    if (!PROFILE_IMAGE_TYPES.has(file.type)) {
      toast({
        title: t("admin.profileImageUploadFailed"),
        description: t("admin.profileImageInvalidType"),
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    if (file.size > PROFILE_IMAGE_MAX_SIZE) {
      toast({
        title: t("admin.profileImageUploadFailed"),
        description: t("admin.profileImageTooLarge"),
        variant: "destructive",
      });
      event.target.value = "";
      return;
    }

    setIsUploadingProfileImage(true);
    try {
      const response = await adminAPI.uploadUserProfileImage(editingUser._id, file);
      setEditingUser(response.data);
      setUsers((currentUsers) =>
        currentUsers.map((user) => user._id === response.data._id ? response.data : user)
      );

      toast({
        title: t("admin.profileImageUpdated"),
        description: response.message || t("admin.profileImageUpdatedDesc"),
      });
    } catch (error: any) {
      toast({
        title: t("admin.profileImageUploadFailed"),
        description: getLocalizedErrorMessage(error, "admin.profileImageUploadFailedDesc"),
        variant: "destructive",
      });
    } finally {
      setIsUploadingProfileImage(false);
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold mb-2 break-words">{t("admin.userManagement")}</h2>
          <p className="text-muted-foreground">{t("admin.userAccountDesc")}</p>
        </div>
        <Button onClick={openCreateDialog} className="w-full md:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          {t("admin.createUser")}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("admin.searchUsers")}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid gap-3 md:hidden">
        {isLoading ? (
          <Card className="p-4 text-center text-muted-foreground">
            {t("admin.loadingUsers")}
          </Card>
        ) : filteredUsers.length === 0 ? (
          <Card className="p-4 text-center text-muted-foreground">
            {t("admin.noUsersFound")}
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <Card key={user._id} className={`p-4 ${!user.isActive ? "opacity-70" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold leading-tight break-words">{user.name}</h3>
                  <p className="mt-1 break-all text-sm text-muted-foreground">{user.email}</p>
                </div>
                <Badge variant={user.isActive ? "default" : "secondary"} className="shrink-0">
                  {user.isActive ? t("admin.active") : t("admin.inactive")}
                </Badge>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="min-w-0">
                  <div className="text-muted-foreground">{t("admin.userRole")}</div>
                  <Badge variant={user.role === "admin" ? "default" : "secondary"} className="mt-1">
                    {user.role === "admin" ? t("admin.roleAdmin") : t("admin.roleUser")}
                  </Badge>
                </div>
                <div className="min-w-0">
                  <div className="text-muted-foreground">{t("admin.userCategory")}</div>
                  <div className="mt-1 break-words font-medium">{t(getCategoryLabel(user.category))}</div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Button variant="outline" size="sm" onClick={() => handleViewProfile(user)} title={t("admin.viewProfile")}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEditDialog(user)} title={t("admin.editUser")}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleStatus(user)}
                  title={user.isActive ? t("admin.deactivateUser") : t("admin.reactivateUser")}
                >
                  {user.isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(user)}
                  disabled={user.role === "admin"}
                  title={user.role === "admin" ? t("admin.adminUserNotDeletable") : t("common.delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <Table className="min-w-[760px]" dir={isRtl ? "rtl" : "ltr"}>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.userName")}</TableHead>
              <TableHead>{t("admin.userEmail")}</TableHead>
              <TableHead>{t("admin.userRole")}</TableHead>
              <TableHead>{t("admin.userCategory")}</TableHead>
              <TableHead>{t("admin.userStatus")}</TableHead>
              <TableHead>{t("admin.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  {t("admin.loadingUsers")}
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {t("admin.noUsersFound")}
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user._id} className={!user.isActive ? "opacity-70" : ""}>
                  <TableCell className="max-w-[180px] break-words font-medium">{user.name}</TableCell>
                  <TableCell className="max-w-[220px] break-all">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                      {user.role === "admin" ? t("admin.roleAdmin") : t("admin.roleUser")}
                    </Badge>
                  </TableCell>
                  <TableCell>{t(getCategoryLabel(user.category))}</TableCell>
                  <TableCell>
                    <Badge variant={user.isActive ? "default" : "secondary"}>
                      {user.isActive ? t("admin.active") : t("admin.inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleViewProfile(user)} title={t("admin.viewProfile")}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} title={t("admin.editUser")}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleStatus(user)}
                        title={user.isActive ? t("admin.deactivateUser") : t("admin.reactivateUser")}
                      >
                        {user.isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(user)}
                        disabled={user.role === "admin"}
                        title={user.role === "admin" ? t("admin.adminUserNotDeletable") : t("common.delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingUser ? t("admin.editUser") : t("admin.createUser")}</DialogTitle>
            <DialogDescription>
              {editingUser ? t("admin.editUserDesc") : t("admin.createUserDesc")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {editingUser && (
              <div className="flex flex-col gap-3 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-14 w-14">
                    <AvatarImage src={editingUser.profileImageDisplayUrl || editingUser.profileImage} alt={editingUser.name} />
                    <AvatarFallback>{editingUser.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="font-medium">{t("admin.profileImage")}</div>
                    <p className="text-sm text-muted-foreground">{t("admin.profileImageHint")}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Input
                    ref={profileImageInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleProfileImageChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => profileImageInputRef.current?.click()}
                    disabled={isUploadingProfileImage}
                    className="w-full sm:w-auto"
                  >
                    <ImageUp className="mr-2 h-4 w-4" />
                    {isUploadingProfileImage ? t("admin.uploadingProfileImage") : t("admin.uploadProfileImage")}
                  </Button>
                </div>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="admin-user-name">{t("admin.userName")}</Label>
              <Input
                id="admin-user-name"
                value={form.name}
                onChange={(event) => handleFormChange("name", event.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="admin-user-email">{t("admin.userEmail")}</Label>
              <Input
                id="admin-user-email"
                type="email"
                value={form.email}
                disabled={!!editingUser}
                onChange={(event) => handleFormChange("email", event.target.value)}
              />
            </div>

            <div className="grid gap-2 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label>{t("admin.userRole")}</Label>
                <Select value={form.role} onValueChange={(value: "user" | "admin") => handleFormChange("role", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">{t("admin.roleUser")}</SelectItem>
                    <SelectItem value="admin">{t("admin.roleAdmin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>{t("admin.userStatus")}</Label>
                <Select value={form.status} onValueChange={(value: "active" | "inactive") => handleFormChange("status", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("admin.active")}</SelectItem>
                    <SelectItem value="inactive">{t("admin.inactive")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>{t("admin.userCategory")}</Label>
                <Select value={form.category} onValueChange={(value: UserCategory) => handleFormChange("category", value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">{t("admin.categoryStudent")}</SelectItem>
                    <SelectItem value="graduate">{t("admin.categoryGraduate")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? t("admin.savingUser") : editingUser ? t("admin.saveUser") : t("admin.createUser")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t("admin.deleteUserTitle")}
        description={t("admin.deleteUserDesc", { name: deleteTarget?.name || "" })}
        confirmText={t("common.delete")}
      />
    </div>
  );
}
